import { useRef, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { mdiCheckCircle, mdiCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";

import { MoneyManagerData, MoneyManagerParticipantEntry } from "api/money-manager";
import { GetUserById } from "api/profile";

function getUnitPrices(data: MoneyManagerData, participantIds: string[]): Record<string, number> {
  const prices: Record<string, number> = {};
  for (const cat of data.categories) {
    if (cat.type === "per_unit") {
      const total = parseFloat(cat.total) || 0;
      const sumUnits = participantIds.reduce(
        (sum, id) => sum + (parseFloat(data.entries[id]?.categories[cat.id] as string) || 0),
        0
      );
      prices[cat.id] = sumUnits > 0 ? total / sumUnits : 0;
    }
  }
  return prices;
}

function getAddonPrices(data: MoneyManagerData, participantIds: string[]): Record<string, number> {
  const prices: Record<string, number> = {};
  for (const cat of data.categories) {
    if (cat.type === "addon") {
      const total = parseFloat(cat.total) || 0;
      const yesCount = participantIds.filter((id) => !!data.entries[id]?.categories[cat.id]).length;
      prices[cat.id] = yesCount > 0 ? total / yesCount : 0;
    }
  }
  return prices;
}

interface ParticipantSummary {
  uid: number;
  name: string;
  catCosts: Record<string, number>;
  totalCost: number;
  advance: number;
  deduction: number;
  balance: number;
  paid: boolean;
}

function calcSummary(data: MoneyManagerData, participants: GetUserById[]): ParticipantSummary[] {
  const participantIds = participants.map((p) => String(p.uid));
  const unitPrices = getUnitPrices(data, participantIds);
  const addonPrices = getAddonPrices(data, participantIds);

  return participants.map((p) => {
    const entry: MoneyManagerParticipantEntry = data.entries[String(p.uid)] ?? {
      categories: {},
      advance: "",
      deduction: "",
      paid: false,
    };
    const catCosts: Record<string, number> = {};
    let totalCost = 0;

    for (const cat of data.categories) {
      let cost = 0;
      if (cat.type === "per_unit") {
        cost = (parseFloat(entry.categories[cat.id] as string) || 0) * (unitPrices[cat.id] ?? 0);
      } else {
        cost = entry.categories[cat.id] ? (addonPrices[cat.id] ?? 0) : 0;
      }
      catCosts[cat.id] = cost;
      totalCost += cost;
    }

    const advance = parseFloat(entry.advance) || 0;
    const deduction = parseFloat(entry.deduction) || 0;
    const balance = totalCost - deduction - advance;

    return { uid: p.uid, name: p.name ?? "", catCosts, totalCost, advance, deduction, balance, paid: entry.paid };
  });
}

function fmt(n: number) {
  return n.toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export interface SummaryProps {
  data: MoneyManagerData;
  isAdmin: boolean;
  participants: GetUserById[];
  onSave: (data: MoneyManagerData) => void;
  userProfileId?: number;
}

export default function Summary(props: SummaryProps) {
  const { data, isAdmin, participants, onSave, userProfileId } = props;
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);

  if (participants.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary' textAlign='center' py={4}>
        No participants have joined this event yet.
      </Typography>
    );
  }

  const rows = calcSummary(data, participants);
  const cur = data.currency;
  const grandTotal = rows.reduce((s, r) => s + r.totalCost, 0);
  const totalAdvances = rows.reduce((s, r) => s + r.advance, 0);
  const totalOwed = rows.filter((r) => !r.paid).reduce((s, r) => s + Math.max(r.balance, 0), 0);
  const paidCount = rows.filter((r) => r.paid).length;

  function togglePaid(uid: number) {
    const profileId = String(uid);
    const entry = data.entries[profileId] ?? { categories: {}, advance: "", deduction: "", paid: false };
    const updated: MoneyManagerData = {
      ...data,
      entries: { ...data.entries, [profileId]: { ...entry, paid: !entry.paid } },
    };
    onSave(updated);
  }

  return (
    <Stack spacing={3}>
      {/* Stats strip */}
      <Box display='flex' gap={1} flexWrap='wrap'>
        {[
          { label: "Paid", value: `${paidCount}/${rows.length}` },
          { label: "Total Advances", value: `${fmt(totalAdvances)} ${cur}` },
          { label: "Total Costs", value: `${fmt(grandTotal)} ${cur}` },
          { label: "Still Owed", value: `${fmt(totalOwed)} ${cur}`, color: "primary" as const },
        ].map((stat) => (
          <Paper key={stat.label} variant='outlined' sx={{ flex: "1 1 140px", p: 2, minWidth: 120 }}>
            <Typography variant='caption' color='text.secondary' sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
              {stat.label}
            </Typography>
            <Typography variant={stat.color ? "h6" : "h6"} color={stat.color ?? "text.primary"} fontWeight={800}>
              {stat.value}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant='outlined'>
        <Table size='small' sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  backgroundColor: "background.paper",
                  boxShadow: (theme) => `inset -1px 0 0 0 ${theme.palette.divider}`,
                }}
              >
                Participant
              </TableCell>
              {data.categories.map((cat) => (
                <TableCell key={cat.id} sx={{ fontWeight: 700 }}>
                  {cat.name}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Deduction</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Advance</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Balance</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Paid</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .sort((a, b) => {
                if (a.uid === userProfileId) return -1;
                if (b.uid === userProfileId) return 1;
                return a.name.localeCompare(b.name);
              })
              .map((r) => (
                <TableRow
                  key={r.uid}
                  onPointerDown={(e) => {
                    pointerStartRef.current = { x: e.clientX, y: e.clientY };
                  }}
                  onClick={(e) => {
                    const start = pointerStartRef.current;
                    pointerStartRef.current = null;
                    if (start) {
                      const moved = Math.hypot(e.clientX - start.x, e.clientY - start.y);
                      if (moved > 10) return;
                    }
                    setSelectedRowId(selectedRowId === r.uid ? null : r.uid);
                  }}
                  sx={(theme) => ({
                    cursor: "pointer",
                    WebkitTapHighlightColor: "transparent",
                    backgroundColor: selectedRowId === r.uid ? "action.selected" : "background.paper",
                    "@media (hover: hover)": {
                      "&:hover": {
                        backgroundColor: selectedRowId === r.uid ? "action.selected" : "action.hover",
                      },
                      ...(selectedRowId !== r.uid && {
                        "&:hover .mm-sticky-cell": {
                          backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
                        },
                      }),
                    },
                  })}
                >
                  <TableCell
                    className='mm-sticky-cell'
                    sx={(theme) => ({
                      opacity: r.paid ? 0.45 : 1,
                      color: r.uid === userProfileId ? "primary.main" : "inherit",
                      position: "sticky",
                      left: 0,
                      zIndex: 2,
                      backgroundColor: "background.paper",
                      boxShadow: `inset -1px 0 0 0 ${theme.palette.divider}`,
                      ...(selectedRowId === r.uid && {
                        backgroundImage: `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
                      }),
                    })}
                  >
                    <strong>{r.name}</strong>
                  </TableCell>
                  {data.categories.map((cat) => (
                    <TableCell key={cat.id} sx={{ opacity: r.paid ? 0.45 : 1 }}>
                      {fmt(r.catCosts[cat.id] ?? 0)}
                    </TableCell>
                  ))}
                  <TableCell sx={{ opacity: r.paid ? 0.45 : 1 }}>
                    <strong>{fmt(r.totalCost)}</strong>
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", opacity: r.paid ? 0.45 : 1 }}>
                    {r.deduction > 0 ? `−${fmt(r.deduction)}` : "—"}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", opacity: r.paid ? 0.45 : 1 }}>
                    {r.advance > 0 ? `−${fmt(r.advance)}` : "—"}
                  </TableCell>
                  <TableCell sx={{ opacity: r.paid ? 0.45 : 1 }}>
                    <Chip
                      label={r.balance <= 0 ? `+${fmt(Math.abs(r.balance))}` : fmt(r.balance)}
                      color={r.balance <= 0 ? "success" : "error"}
                      size='small'
                      variant='outlined'
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size='small'
                      color={r.paid ? "success" : "default"}
                      disabled={!isAdmin}
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePaid(r.uid);
                      }}
                      aria-label={r.paid ? "Mark as unpaid" : "Mark as paid"}
                    >
                      <Icon path={r.paid ? mdiCheckCircle : mdiCircleOutline} size={0.8} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

            {/* Totals row */}
            <TableRow sx={{ "& td": { borderTop: 2, borderColor: "divider", fontWeight: 700 } }}>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 2,
                  backgroundColor: "background.paper",
                  boxShadow: (theme) => `inset -1px 0 0 0 ${theme.palette.divider}`,
                }}
              >
                Totals
              </TableCell>
              {data.categories.map((cat) => (
                <TableCell key={cat.id}>{fmt(rows.reduce((s, r) => s + (r.catCosts[cat.id] ?? 0), 0))}</TableCell>
              ))}
              <TableCell>{fmt(grandTotal)}</TableCell>
              <TableCell>{fmt(rows.reduce((s, r) => s + r.deduction, 0))}</TableCell>
              <TableCell>{fmt(rows.reduce((s, r) => s + r.advance, 0))}</TableCell>
              <TableCell>{fmt(rows.reduce((s, r) => s + r.balance, 0))}</TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
